import React, { useState, useMemo, useCallback, useContext } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import "./PaymentForm.scss";
import { notification, Radio, Space, Alert, Button } from "antd";
import { ProfileContext } from "layouts/private/ProfileContext";
import request from "helper/request";

const PaymentForm = ({
  clientSecret,
  paymentFormRef,
  setOpenPaymentMethodModal,
  setFormSubmitted,
  setIsLoading,
  onSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [formError, setFormError] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [paymentMethodType, setPaymentMethodType] = useState("card");
  const [bankAccountData, setBankAccountData] = useState({
    accountHolderName: "",
    routingNumber: "",
    accountNumber: "",
    accountType: "checking"
  });
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx?.profile, [profileCtx]);
  const setProfile = useMemo(() => profileCtx?.setProfile, [profileCtx]);

  const handleClearFields = useCallback(() => {
    if (elements && paymentMethodType === "card") {
      const cardElement = elements.getElement(CardElement);
      if (cardElement) {
        cardElement.clear();
      }
    }
    setCardholderName("");
    setBankAccountData({
      accountHolderName: "",
      routingNumber: "",
      accountNumber: "",
      accountType: "checking"
    });
  }, [elements, paymentMethodType]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsLoading(true);
      setFormSubmitted(true);
      if (!stripe || !elements) {
        return;
      }

      // For bank account and cashapp, we've already handled the redirect, so just return
      if (paymentMethodType === "us_bank_account" || paymentMethodType === "cashapp") {
        setIsLoading(false);
        setFormSubmitted(false);
        return;
      }

      let result;

      if (paymentMethodType === "card") {
        result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: cardholderName
            }
          }
        });
      }

      if (result.error) {
        setIsLoading(false);
        setFormSubmitted(false);

        // Handle different error types for bank accounts
        if (
          paymentMethodType === "us_bank_account" &&
          result.error.code === "setup_intent_authentication_failure"
        ) {
          setFormError(
            "Bank account verification is required. Please check your bank account details and try again."
          );
        } else {
          setFormError(
            "There was a problem creating your payment method. Please try again."
          );
        }
      } else if (result.setupIntent && result.setupIntent.status === "requires_action") {
        // Bank account might need additional verification
        setIsLoading(false);
        setFormSubmitted(false);
        notification.info({
          message: "Bank Account Added - Verification Required",
          description:
            "Your bank account has been added but may require additional verification for payments."
        });
        handleClearFields();
        setOpenPaymentMethodModal(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        try {
          // Auto-set new payment method as default
          await request.post("/payment-method/auto-set-default");

          const stripePaymentMethods = await request.get(
            "/payment-method/payment-methods"
          );
          if (profile && setProfile) {
            setProfile({
              ...profile,
              paymentMethods: stripePaymentMethods
            });
          }
          notification.success({
            message: "Payment Method Saved and Set as Default!"
          });
          handleClearFields();
          setOpenPaymentMethodModal(false);
          if (onSuccess) {
            onSuccess();
          }
        } catch (err) {
          setIsLoading(false);
          setFormSubmitted(false);
          notification.error({
            message: "Failed to save payment method. Please try again."
          });
          setFormError("Failed to save payment method. Please try again.");
        }
      }
    },
    [
      stripe,
      elements,
      clientSecret,
      cardholderName,
      paymentMethodType,
      bankAccountData,
      setProfile,
      profile,
      handleClearFields,
      setOpenPaymentMethodModal,
      setIsLoading,
      setFormSubmitted,
      onSuccess
    ]
  );

  const style = useMemo(
    () => ({
      base: {
        color: "#000000",
        fontSize: "16px",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        "::placeholder": {
          color: "#cccccc"
        }
      },
      invalid: {
        color: "#df1b41"
      }
    }),
    []
  );

  return (
    <form className="PaymentForm" onSubmit={handleSubmit} ref={paymentFormRef}>
      <div style={{ marginBottom: "20px" }}>
        <Radio.Group
          value={paymentMethodType}
          onChange={(e) => setPaymentMethodType(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="card">Credit/Debit Card</Radio>
            <Radio value="us_bank_account">Bank Account (ACH)</Radio>
            <Radio value="cashapp">Cash App Pay</Radio>
          </Space>
        </Radio.Group>
      </div>

      {paymentMethodType === "card" && (
        <>
          <div>
            <input
              className="CardName"
              type="text"
              id="cardholder-name"
              value={cardholderName}
              placeholder="Name on Card"
              onChange={(e) => {
                const value = e.target.value;
                const filteredValue = value.replace(/[^\p{L}\s]/gu, "");
                setCardholderName(filteredValue);
              }}
              required
            />
          </div>
          <div className="CardWrapper">
            <div className="CardInputWrapper">
              <CardElement className="CardWrapper" options={{ style }} />
            </div>
          </div>
        </>
      )}

      {paymentMethodType === "us_bank_account" && (
        <Alert
          message="Bank Account Management"
          description={
            <div>
              <p>
                You must manage bank accounts directly in Stripe for security and
                verification purposes.
              </p>
              <Button
                type="primary"
                onClick={() => {
                  const confirmed = window.confirm(
                    "You will be redirected to Stripe to manage your bank accounts.\n\nPlease note: It may take up to 2 minutes for Stripe to show all your subscriptions and payment methods.\n\nDo you want to continue?"
                  );

                  if (!confirmed) {
                    return;
                  }

                  request
                    .post("/payment-method/create-portal-session")
                    .then((response) => {
                      window.open(response.url, "_blank");
                      setOpenPaymentMethodModal(false);
                    })
                    .catch(() => {
                      notification.error({
                        message: "Failed to open Stripe portal"
                      });
                    });
                }}
              >
                Manage in Stripe ↗
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
      )}

      {paymentMethodType === "cashapp" && (
        <Alert
          message="Cash App Pay"
          description={
            <div>
              <p>
                You must set up Cash App Pay directly in Stripe for secure payment
                processing.
              </p>
              <Button
                type="primary"
                onClick={() => {
                  const confirmed = window.confirm(
                    "You will be redirected to Stripe to set up Cash App Pay.\n\nPlease note: It may take up to 2 minutes for Stripe to show all your subscriptions and payment methods.\n\nDo you want to continue?"
                  );

                  if (!confirmed) {
                    return;
                  }

                  request
                    .post("/payment-method/create-portal-session")
                    .then((response) => {
                      window.open(response.url, "_blank");
                      setOpenPaymentMethodModal(false);
                    })
                    .catch(() => {
                      notification.error({
                        message: "Failed to open Stripe portal"
                      });
                    });
                }}
              >
                Set up in Stripe ↗
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
      )}

      {formError && <div style={{ color: "red", marginBottom: "15px" }}>{formError}</div>}
      {paymentMethodType === "card" && (
        <div style={{ width: "100%", textAlign: "center", marginTop: "40px" }}>
          By submitting you agree to{" "}
          <a
            href="https://www.goflexio.com/clientterms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and Conditions
          </a>
        </div>
      )}
    </form>
  );
};

export default PaymentForm;
